import { useEffect, useMemo, useRef } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import UsageTracker from 'LearningCenterUI/utils/UsageTracker';
import InteractiveContentError from '../../interactiveEducation/components/InteractiveContentError';
import InteractiveEducationLoading from '../../interactiveEducation/components/InteractiveEducationLoading';
import { LessonPreQuizComplete } from './LessonPreQuizComplete';
import { LessonPreQuizStartScreen } from './LessonPreQuizStartScreen';
import { LessonPreQuizQuestionsContainer } from './LessonPreQuizQuestionsContainer';
import { useLessonPreQuizSession } from '../hooks/useLessonPreQuiz';
import { usePreQuizFromCache } from '../hooks/usePreQuizFromCache';
import { derivePreQuizDisplayStatus, getPreAssessmentTrackingProps } from '../utils/preAssessmentQuizUtils';
import { useLessonQuizMeVideoRequestParams } from '../../interactiveEducation/hooks/useLessonQuizMe';
import { jsx as _jsx } from "react/jsx-runtime";
export const LessonPreQuizContainer = ({
  lesson,
  onSkip,
  onStartLessonWithPreAssessmentFocus,
  screen,
  track
}) => {
  const {
    getLessonPreQuizState,
    startPreQuiz,
    clearPreQuizTasks
  } = useLessonPreQuizSession();
  const {
    allVideosHaveCaptions,
    loading: captionCheckLoading
  } = useLessonQuizMeVideoRequestParams({
    contents: lesson.content
  });
  const quizState = getLessonPreQuizState(lesson.id);
  const displayStatus = derivePreQuizDisplayStatus(quizState);
  const {
    questions
  } = usePreQuizFromCache({
    contents: lesson.content,
    lessonId: lesson.id
  });
  const {
    pageViewProps,
    interactionProps,
    progressProps
  } = useMemo(() => getPreAssessmentTrackingProps({
    lesson,
    track,
    screen
  }), [lesson, track, screen]);
  const hasTrackedPageView = useRef(false);
  useEffect(() => {
    if (hasTrackedPageView.current) {
      return;
    }
    hasTrackedPageView.current = true;
    UsageTracker.trackStandalone('Pageview', Object.assign({}, pageViewProps));
  }, [pageViewProps]);
  const handleStartQuiz = () => startPreQuiz(lesson.id);
  const handleRetry = () => clearPreQuizTasks(lesson.id);
  const quizGenerationUnavailable = !allVideosHaveCaptions && !captionCheckLoading;
  const sectionFlexProps = {
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    maxInlineSize: '100%',
    minBlockSize: '650px'
  };
  if (quizGenerationUnavailable || displayStatus === 'ERROR') {
    return /*#__PURE__*/_jsx(Flex, Object.assign({}, sectionFlexProps, {
      children: /*#__PURE__*/_jsx(InteractiveContentError, {
        handleRetry: handleRetry,
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.preAssessmentQuiz.title"
        })
      })
    }));
  }
  if (displayStatus === 'COMPLETED') {
    return /*#__PURE__*/_jsx(Flex, Object.assign({}, sectionFlexProps, {
      children: /*#__PURE__*/_jsx(LessonPreQuizComplete, {
        interactionProps: interactionProps,
        lessonContent: lesson.content,
        onStartLesson: onStartLessonWithPreAssessmentFocus,
        progressProps: progressProps,
        quizState: quizState
      })
    }));
  }
  if (displayStatus === 'IN_PROGRESS') {
    return /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      flexDirection: "column",
      maxInlineSize: "100%",
      minBlockSize: "650px",
      children: /*#__PURE__*/_jsx(LessonPreQuizQuestionsContainer, {
        interactionProps: interactionProps,
        lessonId: lesson.id,
        onSkip: onSkip,
        progressProps: progressProps,
        questions: questions
      })
    });
  }
  if (displayStatus === 'NOT_STARTED') {
    return /*#__PURE__*/_jsx(Flex, Object.assign({}, sectionFlexProps, {
      children: /*#__PURE__*/_jsx(LessonPreQuizStartScreen, {
        interactionProps: interactionProps,
        onSkip: onSkip,
        onStartQuiz: handleStartQuiz
      })
    }));
  }
  return /*#__PURE__*/_jsx(Flex, Object.assign({}, sectionFlexProps, {
    children: /*#__PURE__*/_jsx(InteractiveEducationLoading, {
      carouselTexts: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.interactiveEducation.loading.quiz.fact1"
      }, "fact1"), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.interactiveEducation.loading.quiz.fact2"
      }, "fact2"), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.interactiveEducation.loading.quiz.fact3"
      }, "fact3"), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.interactiveEducation.loading.quiz.fact4"
      }, "fact4")],
      mainText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.interactiveEducation.loading.buildingQuiz"
      })
    })
  }));
};